import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'; 
import nexapi from '../../services/api';
import { RoleModel } from '../../types/models/RoleModel';

interface RoleState {
  data: RoleModel[];
  totalRecords: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  status: string | null;
}

interface RoleResponse {
  roles: RoleModel[]; // Change to ApplicationModel array instead of any[]
  totalCount: number;
}

// API URL
const API_URL = '/Role';


export const fetchRoles = createAsyncThunk<RoleResponse, { page: number; numberOfRecord: number }>(
    'roles/fetchRoles',
    async ({page, numberOfRecord}) => {
        try{
            const response = await nexapi.post(API_URL + `/GetAll?pagination=true&page=${page}&numberOfRecord=${numberOfRecord}`);
            const data = response.data;
            if(Array.isArray(data?.data)){
                return{
                    roles: data.data,
                    totalCount: data.totalCount,
                };
            }
            else{
                console.error("Invalid API Response:", data);
                return { roles: [], totalCount: 0 }; // Return a fallback structure
            }
        }
        catch(error){
            console.error("API Call Failed:", error);
            return { roles: [], totalCount: 0 };
        }
    }
);

export const fetchActiveRoles = createAsyncThunk<RoleResponse, { page: number; numberOfRecord: number }>(
  'roles/fetchActiveRoles',
  async ({page, numberOfRecord}) => {
      try{
          const response = await nexapi.post(API_URL + `/GetAllActiveRoles`);
          const data = response.data;
          if(Array.isArray(data?.data)){
              return{
                  roles: data.data,
                  totalCount: data.totalCount,
              };
          }
          else{
              console.error("Invalid API Response:", data);
              return { roles: [], totalCount: 0 }; // Return a fallback structure
          }
      }
      catch(error){
          console.error("API Call Failed:", error);
          throw error;
      }
  }
);


export const deleteRole = createAsyncThunk(
  'roles/deleteRole',
  async ({ id, reason }: { id: any; reason: any }) => { // Destructuring and defining types for id and reason
    try {
      // Make the DELETE request with the correct URL format
      await nexapi.delete(`${API_URL}/DeleteRole?id=${id}&reason=${reason}`);
      return id; // Return the role id to be used in the reducer
    } catch (error) {
      return error.message; // Return the error message if request fails
    }
  }
);

export const deleteRoleMap = createAsyncThunk(
  'roles/deleteRoleMap',
  async ({ id, roleId, reason }: { id: any;roleId:any; reason: any }) => { // Destructuring and defining types for id and reason
    try {
      // Make the DELETE request with the correct URL format
      await nexapi.delete(`${API_URL}/DeleteRoleMap?id=${id}&roleId=${roleId}&reason=${reason}`);
      return id; // Return the role id to be used in the reducer
    } catch (error) {
      return error.message; // Return the error message if request fails
    }
  }
);

export const activateRoleMap = createAsyncThunk(
  'roles/activateRoleMap',
  async ({ id, roleId }: { id: any;roleId:any; }) => { // Destructuring and defining types for id and reason
    try {
      // Make the DELETE request with the correct URL format
      await nexapi.patch(`${API_URL}/ActivateRoleMap?id=${id}&roleId=${roleId}`);
      return id;
    } catch (error) {
      return error.message; // Return the error message if request fails
    }
  }
);

const initialState: RoleState = {
    data: [],
    totalRecords: 0,
    currentPage: 0,
    loading: false,
    error: null,
    status: 'idle',
  };

  // Slice
  const rolesSlice = createSlice({
    name: 'roles',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      builder
        .addCase(fetchRoles.fulfilled, (state, action) => {
          // Assuming action.payload contains applications and totalCount
          state.data = action.payload.roles;
          state.totalRecords = action.payload.totalCount;
          state.status = 'succeeded';
        })
        .addCase(fetchRoles.pending, (state) => {
          state.loading = true;
          state.status = 'pending';
        })
        .addCase(deleteRole.fulfilled, (state, action:any) => {
                state.data = state.data.filter((app) => app.id !=  action.payload);
                state.status = 'succeeded';
              }) 
              .addCase(deleteRoleMap.fulfilled, (state, action:any) => {
                state.data = state.data.filter((app) => app.id !=  action.payload);
                state.status = 'succeeded';
              }) 
        .addMatcher(
          (action) => action.type.endsWith('/pending'),
          (state) => {
            state.status = 'loading';
          }
        )
        .addMatcher(
          (action) => action.type.endsWith('/rejected'),
          (state, action) => {
            state.status = 'failed';          
          }
        );
    },
  });
  
  export default rolesSlice.reducer;